import Cookies from "js-cookie";
import { React, useState, useEffect, useRef, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";

import Navbar from "../../../Components/Navbar";
import { fetchProfile } from "../../../utils/fetchProfile";
import ClosedStatusPin from "../../../assets/images/ClosedStatusPin.svg";
import OnProgressStatusPin from "../../../assets/images/OnProgressStatusPin.svg";
import ValidStatusPin from "../../../assets/images/ValidStatusPin.svg";
import NotStartedStatusPin from "../../../assets/images/NotStartedStatusPin.svg";

import MapLayout from "../MapLayout";
import L from "leaflet";
import * as turf from "@turf/turf";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { Button, Box, Card, Typography, CircularProgress } from "@mui/material";

const provinceMap = {
  11: "ACEH",
  12: "SUMATERA_UTARA",
  13: "SUMATERA_BARAT",
  14: "RIAU",
  15: "JAMBI",
  16: "SUMATERA_SELATAN",
  17: "BENGKULU",
  18: "LAMPUNG",
  19: "KEPULAUAN_BANGKA_BELITUNG",
  21: "KEPULAUAN_RIAU",
  31: "DKI_JAKARTA",
  32: "JAWA_BARAT",
  33: "JAWA_TENGAH",
  34: "DAERAH_ISTIMEWA_YOGYAKARTA",
  35: "JAWA_TIMUR",
  36: "BANTEN",
  51: "BALI",
  52: "NUSA_TENGGARA_BARAT",
  53: "NUSA_TENGGARA_TIMUR",
  61: "KALIMANTAN_BARAT",
  62: "KALIMANTAN_TENGAH",
  63: "KALIMANTAN_SELATAN",
  64: "KALIMANTAN_TIMUR",
  65: "KALIMANTAN_UTARA",
  71: "SULAWESI_UTARA",
  72: "SULAWESI_TENGAH",
  73: "SULAWESI_SELATAN",
  74: "SULAWESI_TENGGARA",
  75: "GORONTALO",
  76: "SULAWESI_BARAT",
  81: "MALUKU",
  82: "MALUKU_UTARA",
  91: "PAPUA",
  92: "PAPUA_BARAT",
};

const RectangleIcon = ({ color }) => (
  <div
    style={{
      width: "20px",
      height: "20px",
      backgroundColor: color || "gray",
      marginRight: "3px",
    }}
  ></div>
);

const LegendItem = ({ color, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "4px",
      marginRight: "5px",
    }}
  >
    <RectangleIcon color={color} />
    {label}
  </div>
);

function MapSurveyorRegency() {
  const { provinceId, regencyId, mhid } = useParams();
  const accessToken = Cookies.get("access_token") || "";
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);
  const [dataProfile, setDataProfile] = useState([]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedSurveyId = queryParams.get("survey_id") || "";
  const selectedPeriodeId = queryParams.get("periode") || "";
  const selectedArea = queryParams.get("area") || "";

  const isMounted = useRef(true);

  const [dataDashboard, setDataDashboard] = useState([]);

  const [surveyDataId, setSurveyDataId] = useState([]);
  const [formattedLabelName, setFormattedLabelName] = useState([]);
  const [totalPlanCount, setTotalPlanCount] = useState(0);

  const [filteredGeoJSONData, setFilteredGeoJSONData] = useState(null);

  const [mapPosition, setMapPosition] = useState([-2.548, 112.544]);
  const [mapZoom, setMapZoom] = useState(5);

  useEffect(() => {
    isMounted.current = true;

    const controller = new AbortController();
    const { signal } = controller;

    const fetchDataAndMap = async () => {
      setIsLoading(true);
      setFilteredGeoJSONData(null);
      setSurveyDataId([]);

      try {
        let allDashboardData = [];
        let currentPage = 0;
        let totalPages = 1;
        const size = 20;

        while (currentPage < totalPages) {
          if (!isMounted.current) break;

          const queryParams = new URLSearchParams();
          queryParams.append("survey_id", selectedSurveyId);
          queryParams.append("province_code", provinceId);
          queryParams.append("regency_code", `${provinceId}${regencyId}`);
          queryParams.append("page", currentPage + 1);
          queryParams.append("size", size);

          const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/audit-plan?${queryParams}`;
          const mainRes = await fetch(apiUrl, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal,
          });

          if (mainRes.ok) {
            const result = await mainRes.json();
            const pageData = result.data || [];
            allDashboardData.push(...pageData);

            if (result.pagination_info) {
              totalPages = result.pagination_info.total_pages;
            } else {
              totalPages = 0;
            }
            currentPage++;
          } else {
            console.error(`Failed to fetch audit plan page ${currentPage}`);
            if (isMounted.current) setDataDashboard([]);
            break;
          }
        }

        let dashboardData = allDashboardData;
        if (isMounted.current) {
          setDataDashboard(dashboardData);
        }

        if (dashboardData.length && provinceId && mhid) {
          const mapUrl = `${
            process.env.REACT_APP_API_MAP_URL
          }/api/v1/regencies/${encodeURIComponent(mhid)}`;
          const geoRes = await fetch(mapUrl, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal,
          });

          if (geoRes.ok) {
            const geojsonFeature = await geoRes.json();

            const updatedFeature = {
              ...geojsonFeature,
              properties: {
                ...geojsonFeature.properties,
                total_plan_sum: dashboardData.length,
                store_count: dashboardData.length,
              },
            };

            const finalGeoJSON = {
              type: "FeatureCollection",
              features: [updatedFeature],
            };

            if (isMounted.current) {
              setFilteredGeoJSONData(finalGeoJSON);

              const markers = dashboardData.map((store) => {
                const store_status = store.audit_status;
                return {
                  id: store.store_code || store.id,
                  name: store.store_name,
                  address: store.store_address,
                  tipe_toko: store.store_type,
                  tipe_survey: store.tipe_survey,
                  latitude: store.store_longitude,
                  longitude: store.store_latitude,
                  audit_status: store.audit_status,
                  store_status,
                  icon: MapIcon({ store_status }),
                };
              });
              setSurveyDataId(markers);
            }
          } else {
            console.error("Failed to fetch GeoJSON data");
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error fetching data/map:", err);
        }
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchDataAndMap();
    }, 300);

    return () => {
      isMounted.current = false;
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [
    provinceId,
    regencyId,
    selectedSurveyId,
    selectedPeriodeId,
    selectedArea,
    mhid,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      try {
        if (filteredGeoJSONData && filteredGeoJSONData.features.length > 0) {
          const bbox = turf.bbox(filteredGeoJSONData);

          if (!bbox.every(isFinite)) {
            console.error(
              "Invalid bounding box calculated. Check GeoJSON data for invalid features.",
              filteredGeoJSONData
            );
            return;
          }

          const center = [(bbox[1] + bbox[3]) / 2, (bbox[0] + bbox[2]) / 2];

          if (isNaN(center[0]) || isNaN(center[1])) {
            console.error("Calculated center is NaN. Bounding box was:", bbox);
            return;
          }

          const bboxWidth = bbox[2] - bbox[0];
          const bboxHeight = bbox[3] - bbox[1];
          const maxDim = Math.max(bboxWidth, bboxHeight);

          const zoom = Math.floor(10 - Math.log2(maxDim));

          setMapPosition(center);
          setMapZoom(zoom);

          const labelName =
            filteredGeoJSONData.features[0].properties.name || "Unknown Area";
          const formattedName = labelName
            .toLowerCase()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          setFormattedLabelName(formattedName);
        }
      } catch (error) {
        console.error("Error calculating map bounds:", error);
      }
    }, 100);
    return () => {
      clearTimeout(handler);
    };
  }, [filteredGeoJSONData]);

  useEffect(() => {
    if (dataDashboard && dataDashboard.length > 0) {
      const total = dataDashboard.length;
      setTotalPlanCount(total);
    }
  }, [dataDashboard]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProfile(accessToken, setDataProfile, setNotAllowAccessPage, []);
    }, 100);
    return () => {
      clearTimeout(handler);
    };
  }, [accessToken]);

  const onEachRegencyFeature = (feature, layer) => {
    const provinceName = feature.properties.name;

    layer.bindTooltip(`${provinceName}`, {
      permanent: true,
      direction: "center",
      className: "province-label",
    });

    // layer.on({
    //   click: (e) => {
    //     const mhid = feature.properties.kabkot_id;
    //     if (provinceId && mhid) {
    //       window.location.href = `/map-surveyor/${provinceId}/${mhid}`;
    //     } else {
    //       console.error("Missing province or regency ID");
    //     }
    //   },
    // });
  };

  const exportToPDF = () => {
    setIsLoading(true);

    const inputElements = document.getElementsByClassName("printToPdfArea");

    if (inputElements.length === 0) {
      console.error('Element with class "printToPdfArea" not found');
      setIsLoading(false);
      return;
    }

    const input = inputElements[0];

    html2canvas(input, { scale: 1.5 })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const imgWidth = canvas.width * 0.75;
        const imgHeight = canvas.height * 0.75;
        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const title = `peta_sebaran_toko_${(
          provinceMap[provinceId] || "Unknown Regency"
        )
          .toLowerCase()
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}_0_toko`;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);

        const titleLines = pdf.splitTextToSize(title, pageWidth - 20);

        let yPosition = 6;
        titleLines.forEach((line) => {
          const textWidth = pdf.getTextWidth(line);
          const xPosition = (pageWidth - textWidth) / 2;
          pdf.text(line, xPosition, yPosition);
          yPosition += 6;
        });

        const imageTopPosition = yPosition + 10;
        const imageHeight = (imgHeight * pageWidth) / imgWidth;

        if (imageHeight <= pageHeight - imageTopPosition) {
          pdf.addImage(
            imgData,
            "JPEG",
            0,
            imageTopPosition,
            pageWidth,
            imageHeight
          );
        } else {
          let heightLeft = imageHeight;
          let position = imageTopPosition;

          while (heightLeft > 0) {
            pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imageHeight);
            heightLeft -= pageHeight;
            if (heightLeft > 0) {
              pdf.addPage();
              position = 0;
            }
          }
        }

        pdf.save(`${title}.pdf`);
      })
      .catch((error) => {
        console.error("Error exporting to PDF:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const MapIcon = (mapData) => {
    let iconUrl = "";

    switch (mapData.store_status) {
      case "Closed":
        iconUrl = ClosedStatusPin;
        break;
      case "On Progress":
        iconUrl = OnProgressStatusPin;
        break;
      case "Valid":
        iconUrl = ValidStatusPin;
        break;
      case "Not Started":
        iconUrl = NotStartedStatusPin;
        break;
      default:
        iconUrl = "default-icon-url.png";
        break;
    }

    const icon = L.icon({
      iconUrl: iconUrl,
      iconSize: [15, 15],
      className: "custom-icon",
    });

    return icon;
  };

  const detailMasterData = (mapData) => {
    return (
      <div className="custom-popup-content">
        <div>
          <strong>Kode Outlet :</strong> {mapData.id}
        </div>
        <div>
          <strong>Nama Outlet :</strong> {mapData.name}
        </div>
        <div>
          <strong>Tipe Outlet :</strong> {mapData.tipe_toko}
        </div>
        <div>
          <strong>Tipe Survey :</strong> {mapData.tipe_survey}
        </div>
        <div>
          <strong>Alamat :</strong> {mapData.address}
        </div>
        <div>
          <strong>Latitude :</strong> {mapData.latitude}
        </div>
        <div>
          <strong>Longitude :</strong> {mapData.longitude}
        </div>
      </div>
    );
  };

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="Dashboard"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div>Not allow to access this page</div>
      </div>
    );
  }
  return (
    <div>
      <Navbar
        active="Dashboard"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <div className="content printToPdfArea" style={{ padding: "1rem" }}>
        <div className="legend">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: { xs: "flex-start", sm: "space-between" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <Button
              className="button-pros"
              sx={{
                backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                border: "1px solid hsl(186, 89.90%, 27.30%)",
                borderRadius: "4px",
                color: "#fff",
                fontSize: "14px",
                height: "fit-content",
                textTransform: "none",
                "&:hover": {
                  backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                },
              }}
              disabled={isLoading}
              onClick={() => window.history.back()}
            >
              Back to Previous
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontSize: 20,
                fontFamily: "Montserrat",
                fontWeight: 700,
              }}
            >
              Peta sebaran Toko di {formattedLabelName}{" "}
              {totalPlanCount ? `(${totalPlanCount} Toko)` : ""}
            </Typography>
            <Button
              className="button-pros"
              sx={{
                backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                border: "1px solid hsl(186, 89.90%, 27.30%)",
                borderRadius: "4px",
                color: "#fff",
                fontSize: "14px",
                height: "fit-content",
                textTransform: "none",
                "&:hover": {
                  backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                },
              }}
              onClick={exportToPDF}
              disabled={isLoading}
            >
              Download as PDF
            </Button>
          </Box>
          <Card sx={{ m: 0.5, mt: 3, backgroundColor: "#f0fbfcff" }}>
            <div>
              {isLoading || !filteredGeoJSONData ? (
                <Box
                  sx={{
                    height: "700px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={60} thickness={5} />
                </Box>
              ) : (
                <MapLayout
                  markersData={{
                    data: surveyDataId,
                    icon: MapIcon,
                  }}
                  position={mapPosition}
                  zoom={mapZoom}
                  pathOptions={{
                    color: "#4f4f4fff",
                    weight: 1,
                    fillColor: "#e0f7ffff",
                    fillOpacity: 0.5,
                  }}
                  scrollWheelZoom={true}
                  styleContainer={{ width: "100%", height: "700px" }}
                  renderDataDetail={detailMasterData}
                  geoJSONData={filteredGeoJSONData}
                  onEachFeature={onEachRegencyFeature}
                  needLegend={true}
                  needChoropleth={false}
                />
              )}
            </div>
          </Card>
        </div>
      </div>
      <style>{`
        .province-label {
          background-color: transparent;
          border: none;
          box-shadow: none;
          font-weight: 900;
          font-size: 12px;
          color: #333;
            text-shadow: 
                1px 1px 0 white, 
            -1px 1px 0 white, 
                1px -1px 0 white, 
            -1px -1px 0 white; 
            }
      `}</style>
    </div>
  );
}

export default MapSurveyorRegency;
